import { useContext, useEffect, useState } from "react";
import { BiDownArrow, BiRightArrow } from "react-icons/bi";
import { GrAdd } from "react-icons/gr";
import { FiEdit3 } from "react-icons/fi";
import { BsTrash } from "react-icons/bs";
import YesNoPrompt from "../../utility/YesNoPrompt";
import SectionModal from "./SectionModal";
import FieldModal from "../field/FieldModal";
import Field from "../field/Field";
import CloneSectionModal from "./CloneSectionModal";
import { ImCopy } from "react-icons/im";
import LoadingCircle from "../../utility/LoadingCircle";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { MdOutlineSchema } from "react-icons/md";
import SectionInfoModal from "./SectionInfoModal";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import UserContext from "../../User/UserContext";
import PermissionGuard from "../../User/PermissionGuard";
import PermissionsMap from "../../User/PermissionsMap";

const Section = ({
	deleteSection,
	idsOfOpenSections,
	setIdsOfOpenSections,
	fieldTypes,
	template_id,
	fieldWordBank,
	sectionWordBank,
	id,
	name,
	allSectionData,
	position,
	sections,
	setSections,
	isDrafting,
	disableSectionDrag,
	setDisableSectionDrag,
	odooModelMapping,
}) => {
	const { api, token } = useContext(UserContext);
	const [fields, setFields] = useState([]);
	const [showSection, setShowSection] = useState(false);
	const [showDeletePrompt, setShowDeletePrompt] = useState(false);
	const [showUpdateModal, setShowUpdateModal] = useState(false);
	const [showCreateFieldModal, setShowCreateFieldModal] = useState(false);
	const [showCloneModal, setShowCloneModal] = useState(false);
	const [loadingFields, setLoadingFields] = useState(false);
	const [showSectionInfoModal, setShowSectionInfoModal] = useState(false);
	const { userPermissions } = useContext(UserContext);
	const hasPermissionToReorder = userPermissions?.includes("PermissionsMap.TEMPLATES_UPDATE");

	useEffect(() => {
		if (idsOfOpenSections.includes(id)) {
			setShowSection(true);
		} else {
			setShowSection(false);
		}
	}, [id, idsOfOpenSections]);

	useEffect(() => {
		if (!api || !token) {
			return;
		}

		const controller = new AbortController();
		const signal = controller.signal;
		setLoadingFields(true);
		fetch(api + "/section/" + id, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			signal,
		})
			.then((res) => {
				if (!res.ok) {
					setLoadingFields(false);
					// put an error here
				} else {
					return res.json();
				}
			})
			.then((res) => {
				if (res) {
					setLoadingFields(false);
					const unsortedFields = res.fields;
					const sortedFields = unsortedFields.sort((a, b) => a.position - b.position);
					setFields(sortedFields);
				} else {
					setLoadingFields(false);
				}
			})
			.catch((err) => {});

		return () => {
			controller.abort();
		};
	}, [api, token, id]);

	const updateFieldInDB = (theField, newPosition) => {
		fetch(api + "/field/move/" + theField.id, {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			body: JSON.stringify({ position: newPosition }),
		}).then((res) => {
			if (!res.ok) {
				// put an error here
			} else {
			}
		});
	};

	const updateMultipleFieldsInDB = (updatedFields) => {
		fetch(api + "/field/move-multiple", {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: "Bearer " + token,
			},
			body: JSON.stringify({ updatedFields }),
		}).then((res) => {
			if (!res.ok) {
				// put an error here
			} else {
			}
		});
	};

	const handleOnDragEnd = (result) => {
		if (!result.destination) return;
		const items = Array.from(fields);
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);
		items.forEach((item, index) => {
			item.position = index;
		});
		updateMultipleFieldsInDB(items);
		setFields(items);
	};

	return (
		<>
			<div className="z-50">
				{showCreateFieldModal ? (
					<FieldModal
						isCreatingField={true}
						section_id={id}
						fieldTypes={fieldTypes}
						fieldWordBank={fieldWordBank}
						fields={fields}
						setFields={setFields}
						setShowCreateFieldModal={setShowCreateFieldModal}
						odooModelMapping={odooModelMapping}
					/>
				) : null}
				{showSectionInfoModal ? (
					<SectionInfoModal
						sectionName={name}
						setShowSectionInfoModal={setShowSectionInfoModal}
						condition_type={allSectionData.condition_type}
						condition_value={allSectionData.condition_value}
						field_condition_id={allSectionData.field_condition_id}
						section_condition_id={allSectionData.section_condition_id}
						sections={sections}
						fields={fields}
					/>
				) : null}
				{showCloneModal ? (
					<CloneSectionModal
						template_id={template_id}
						sectionName={name}
						setShowCloneModal={setShowCloneModal}
						sectionWordBank={sectionWordBank}
						id={id}
						section_id={id}
						sections={sections}
						setSections={setSections}
					/>
				) : null}
				{showUpdateModal ? (
					<SectionModal
						position={position}
						isCreatingSection={false}
						currentName={name}
						setShowSectionModal={setShowUpdateModal}
						sectionWordBank={sectionWordBank}
						section_id={id}
						sections={sections}
						setSections={setSections}
						condition_type={allSectionData.condition_type}
						condition_value={allSectionData.condition_value}
						field_condition_id={allSectionData.field_condition_id}
						section_condition_id={allSectionData.section_condition_id}
					/>
				) : null}
				{showDeletePrompt ? (
					<YesNoPrompt type="Section" name={name} deleteAction={deleteSection} cancelAction={() => setShowDeletePrompt(false)} />
				) : null}
			</div>
			<div className="mb-2 relative">
				<div className="flex justify-between bg-gray-300 border border-black sticky -top-1">
					<div className="text-xl font-bold p-3 flex items-center">
						<p>
							{showSection ? (
								<span
									className="cursor-pointer select-none"
									onClick={() => {
										setShowSection(false);
										setDisableSectionDrag(false);
										setIdsOfOpenSections((prev) => [...prev.filter((filterData) => filterData !== id)]);
									}}>
									<BiDownArrow />
								</span>
							) : (
								<span
									className="cursor-pointer select-none"
									onClick={() => {
										setShowSection(true);
										setDisableSectionDrag(true);
										if (idsOfOpenSections.includes(id) === false) {
											setIdsOfOpenSections((prev) => [...prev, id]);
										}
									}}>
									<BiRightArrow />
								</span>
							)}
						</p>
						<p id={name.replaceAll(" ", "-")}>Section: {name}</p>
					</div>

					{isDrafting ? (
						<div className="flex justify-center items-center text-2xl p-4 gap-4 ">
							{allSectionData.section_condition_id && allSectionData.field_condition_id ? (
								<MdOutlineSchema
									title="Displayed conditionally"
									color="black"
									className="cursor-pointer select-none"
									onClick={() => setShowSectionInfoModal(true)}
								/>
							) : null}
							<PermissionGuard permission={PermissionsMap.TEMPLATES_DELETE}>
								<BsTrash className="cursor-pointer" color="red" onClick={() => setShowDeletePrompt(true)} />
							</PermissionGuard>
							<PermissionGuard permission={PermissionsMap.TEMPLATES_UPDATE}>
								<FiEdit3 className="cursor-pointer" onClick={() => setShowUpdateModal(true)} />
							</PermissionGuard>
							<PermissionGuard permission={PermissionsMap.TEMPLATES_CREATE}>
								<GrAdd className="cursor-pointer" onClick={() => setShowCreateFieldModal(true)} />
							</PermissionGuard>
							<PermissionGuard permission={PermissionsMap.TEMPLATES_UPDATE}>
								<ImCopy className="cursor-pointer" onClick={() => setShowCloneModal(true)} />
							</PermissionGuard>
						</div>
					) : (
						<div className="flex md:justify-center items-center text-2xl p-3 gap-3">
							{allSectionData.section_condition_id && allSectionData.field_condition_id ? (
								<MdOutlineSchema
									title="Displayed conditionally"
									color="black"
									className="cursor-pointer select-none"
									onClick={() => setShowSectionInfoModal(true)}
								/>
							) : null}
							<AiOutlineInfoCircle className="cursor-pointer select-none" onClick={() => setShowSectionInfoModal(true)} />
						</div>
					)}
				</div>
				<DragDropContext onDragEnd={handleOnDragEnd}>
					<Droppable droppableId="fields">
						{(provided) => (
							<ul className="fields" {...provided.droppableProps} ref={provided.innerRef}>
								{fields && fields.length > 0 && showSection ? (
									fields.map((values, index) => {
										return (
											<Draggable
												key={values.id}
												draggableId={values.id.toString()}
												index={index}
												isDragDisabled={!isDrafting || !hasPermissionToReorder}>
												{(provided) => (
													<div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
														<Field
															key={values.id}
															arrayIndex={index}
															section_id={id}
															fieldId={values.id}
															values={values}
															className="p-4"
															fieldTypes={fieldTypes}
															fields={fields}
															setFields={setFields}
															fieldWordBank={fieldWordBank}
															isDrafting={isDrafting}
															updateFieldInDB={updateFieldInDB}
															odooModelMapping={odooModelMapping}></Field>
													</div>
												)}
											</Draggable>
										);
									})
								) : showSection ? (
									loadingFields ? (
										<div className="flex flex-col items-center justify-center w-full my-2">
											<LoadingCircle />
											<p>Loading Fields</p>
										</div>
									) : (
										<p className="p-2">This section is empty</p>
									)
								) : null}
								{provided.placeholder}
							</ul>
						)}
					</Droppable>
				</DragDropContext>
			</div>
		</>
	);
};

export default Section;
